import React, { useState, useEffect, useRef } from "react";
import { Layers3, X, Expand, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

// Cross-section layer diagram (used in hover overlay and lightbox)
const CrossSection = ({ product, mode }) => (
  <div className={mode === "lightbox" ? "flex flex-col p-4" : "flex flex-col p-3 h-full"}>
    <p className="text-[9px] font-bold tracking-[0.2em] text-[#f4520b] text-center mb-1.5 shrink-0">
      CROSS-SECTION VIEW
    </p>
    {mode === "lightbox" ? (
      <div className="flex flex-col gap-1.5">
        {product.layers.map((l) => (
          <div
            key={l.label}
            className="rounded-md flex items-center justify-center border border-black/5 shrink-0"
            style={{ height: `${Math.max(48, l.pct * 2.6)}px`, backgroundColor: l.bg }}
          >
            <span className="text-[11px] font-semibold px-2 text-center" style={{ color: l.text }}>
              {l.label}
            </span>
          </div>
        ))}
      </div>
    ) : (
      <div className="flex-1 flex flex-col gap-[3px]">
        {product.layers.map((l) => (
          <div
            key={l.label}
            className="rounded-[3px] flex items-center justify-center border border-black/5"
            style={{ flexGrow: l.pct, flexBasis: 0, backgroundColor: l.bg }}
          >
            <span className="text-[8.5px] leading-none font-semibold truncate px-1" style={{ color: l.text }}>
              {l.label}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Expandable gallery lightbox: main photo + cross-section as separate scrollable slides
const ImageLightbox = ({ product, onClose }) => {
  const scrollRef = useRef(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goTo(1);
      if (e.key === "ArrowLeft") goTo(0);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const goTo = (i) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;
    setSlide(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden select-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-extrabold text-[#1c2e3a] truncate">{product.name}</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">{product.type}</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 h-9 w-9 rounded-full bg-[#1c2e3a] text-white flex items-center justify-center hover:bg-[#29424f] transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable gallery */}
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex overflow-x-auto snap-x snap-mandatory h-[380px] md:h-[440px]"
            style={{ scrollbarWidth: "none" }}
          >
            {/* Slide 1: main product image */}
            <div className="relative w-full h-full shrink-0 snap-center bg-slate-100">
              <img
                src={product.image}
                alt={product.name}
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              />
              <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 bg-white/90 backdrop-blur-sm text-slate-700 rounded-md shadow-sm">
                {product.type}
              </span>
              <span className="absolute bottom-3 left-3 text-[11px] font-semibold px-2.5 py-1 bg-[#1c2e3a]/85 text-white rounded-md backdrop-blur-sm">
                Product Photo
              </span>
            </div>

            {/* Slide 2: cross-section (real image, or layer diagram fallback) */}
            <div className="relative w-full h-full shrink-0 snap-center bg-white overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
              {product.crossImage ? (
                <div className="relative w-full h-full min-h-full">
                  <img
                    src={product.crossImage}
                    alt={`${product.name} cross-section`}
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  />
                  <span className="absolute bottom-3 left-3 text-[11px] font-semibold px-2.5 py-1 bg-[#1c2e3a]/85 text-white rounded-md backdrop-blur-sm">
                    Cross-Section
                  </span>
                </div>
              ) : (
                <CrossSection product={product} mode="lightbox" />
              )}
            </div>
          </div>

          {/* Arrows */}
          {slide > 0 && (
            <button
              onClick={() => goTo(0)}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-md flex items-center justify-center text-[#1c2e3a] hover:bg-white transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {slide < 1 && (
            <button
              onClick={() => goTo(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-md flex items-center justify-center text-[#1c2e3a] hover:bg-white transition-colors"
              aria-label="Next"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>

        {/* Dots + labels */}
        <div className="flex items-center justify-center gap-6 py-3 border-t border-slate-100">
          {["Photo", "Cross-Section"].map((label, i) => (
            <button key={label} onClick={() => goTo(i)} className="flex items-center gap-2 group">
              <span className={`h-2 w-2 rounded-full transition-colors ${slide === i ? "bg-[#f4520b]" : "bg-slate-300 group-hover:bg-slate-400"}`} />
              <span className={`text-[12px] font-medium ${slide === i ? "text-[#1c2e3a]" : "text-slate-400"}`}>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function ProductRow({ product, onEnquire }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const startPrice = product.startPrice;

  const blockCopy = (e) => e.preventDefault();

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col md:flex-row items-start md:items-center gap-6 group">
      {/* Left: image - hover reveals cross-section, click expands */}
      <div
        className="group/img relative w-full md:w-56 h-48 sm:h-44 shrink-0 bg-slate-100 rounded-xl overflow-hidden border border-slate-100 cursor-pointer select-none"
        onClick={() => setLightboxOpen(true)}
        onContextMenu={blockCopy}
        title="Click to expand"
      >
        <img
          src={product.image}
          alt={product.name}
          draggable={false}
          className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-300 pointer-events-none"
        />
        <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 bg-white/90 backdrop-blur-sm text-slate-700 rounded-md shadow-sm z-10">
          {product.type}
        </span>
        {/* Expand icon */}
        <span className="absolute top-2 right-2 z-10 h-6 w-6 rounded-md bg-[#1c2e3a]/85 text-white backdrop-blur-sm flex items-center justify-center">
          <Expand size={12} />
        </span>
        {/* Hover hint */}
        <span className="absolute bottom-2 right-2 z-10 text-[10px] font-semibold px-2 py-1 rounded-md bg-[#1c2e3a]/85 text-white backdrop-blur-sm flex items-center gap-1 transition-opacity duration-200 group-hover/img:opacity-0">
          <Layers3 size={11} /> View layers
        </span>
        {/* Cross-section overlay (desktop hover) */}
        <div className="absolute inset-0 bg-white transition-opacity duration-300 z-10 opacity-0 group-hover/img:opacity-100">
          {product.crossImage ? (
            <>
              <img
                src={product.crossImage}
                alt={`${product.name} cross-section`}
                draggable={false}
                className="w-full h-full object-cover pointer-events-none"
              />
              <span className="absolute bottom-2 left-2 text-[10px] font-semibold px-2 py-1 rounded-md bg-[#1c2e3a]/85 text-white backdrop-blur-sm">
                Cross-Section View
              </span>
            </>
          ) : (
            <CrossSection product={product} mode="row" />
          )}
        </div>
      </div>

      {/* Middle: info */}
      <div className="flex-grow space-y-2 w-full">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-bold text-[#1c2e3a]">{product.name}</h3>
          <span className="text-xs font-medium px-2.5 py-1 rounded-full border text-emerald-700 bg-emerald-50 border-emerald-200">Bulk Ready</span>
        </div>
        <p className="text-slate-600 text-sm leading-relaxed">{product.desc}</p>
      </div>

      {/* Right: starting price + enquire */}
      <div className="w-full md:w-64 flex flex-col gap-3 border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-6 shrink-0">
        <div className="md:text-right">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide block">Starts from</span>
          <div className="text-2xl font-black text-[#1c2e3a] leading-tight">₹{startPrice.toLocaleString("en-IN")}</div>
          <span className="text-[11px] text-slate-400">+ GST · bulk discounts on enquiry</span>
        </div>
        <Button onClick={() => onEnquire(product.displayName)} className="w-full bg-[#1c2e3a] hover:bg-[#29424f] text-white font-medium rounded-xl text-sm h-10">
          Enquire Now
        </Button>
      </div>

      {lightboxOpen && <ImageLightbox product={product} onClose={() => setLightboxOpen(false)} />}
    </div>
  );
}
